import React, { useState, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Box } from "@mui/material";

// --- Material Dashboard Pro React Components ---
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

// --- Styles from loginPageStyle.js for consistent look ---
import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";

const useStyles = makeStyles(styles);

export default function PrivacyPolicy() {
  const [cardAnimation, setCardAnimation] = useState("cardHidden");
  const isMountedRef = useRef(true);
  const classes = useStyles();

  // --- Card Animation Effect ---
  useEffect(() => {
    isMountedRef.current = true;
    const id = setTimeout(() => {
      if (isMountedRef.current) setCardAnimation("");
    }, 300);

    return () => {
      isMountedRef.current = false;
      clearTimeout(id);
    };
  }, []);

  return (
    <div className={classes.container}>
      <GridContainer justify="center">
        <GridItem xs={12} sm={10} md={8}>
          <Card login className={classes[cardAnimation]}>
            <CardHeader
              className={`${classes.cardHeader} ${classes.textCenter}`}
              color="rose"
            >
              <h4 className={classes.cardTitle}>Privacy Policy</h4>
            </CardHeader>
            <CardBody>
              {/* --- Original Privacy Policy Content Starts Here --- */}
              <Box sx={{ m: 0, p: 0 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Protecting your data is a priority for us
                </Typography>

                <Typography variant="body2" gutterBottom fontWeight="bold">
                  Last Update: 1st August 2025
                </Typography>

                <Box component="dl" mt={4}>
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (1). Accessing the Beramour Application and Embedded
                      Dating Services.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Welcome to Beramour ("Beramour", "we", "our", or "us"). The
                    Beramour Dating Platform is a product of Beta Infinity Ltd.,
                    hereafter referred to as "the Company". By accessing our
                    services—whether through a mobile device, tablet, computer,
                    or any other communication medium capable of presenting our
                    dating application—you agree to be bound by the following:
                    <ol type="a">
                      <li>Terms of Use,</li>
                      <li>Privacy Policy, and</li>
                      <li>Affiliate Policy.</li>
                    </ol>
                  </Typography>
                  <Typography component="dd" paragraph>
                    At Beramour, we are committed to protecting your privacy and
                    ensuring the security of your personal information. This
                    Privacy Policy outlines how we collect, use, disclose, and
                    safeguard your information when you use our web or mobile
                    application, or engage with our affiliate marketing module.
                    By accessing and using our services, you consent to the
                    collection and use of your personal data in accordance with
                    the terms of this Privacy Policy.
                  </Typography>

                  <Typography component="dd" paragraph>
                    By registering an account or logging in as a member on any
                    of our dating platforms, you consent to the collection and
                    use of your personal data. We may amend these Terms of Use,
                    Privacy Policy, and Affiliate Policy from time to time
                    without prior notice. The date of the last amendment is
                    shown at the beginning of each relevant document. You
                    further agree that you are solely responsible for ensuring
                    that you regularly review the current versions of these
                    three subject matter agreements. If you do not agree with
                    our Privacy Policy, kindly cease to access and/or use our
                    services immediately.
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(2). The Information We Collect.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    <strong>(2.1) Personal Information</strong>
                    <ol type="a">
                      <li>
                        Upon Registration and Login: When you register or log in
                        to our services, we collect minimal personal details
                        such as your name, nickname, gender, location, telephone
                        number, email address, and password.
                      </li>
                      <li>
                        Upon Creation of Affiliate Accounts: If you create an
                        affiliate marketing account, we collect additional
                        information including, but not limited to, your business
                        name, payment details, and contact information.
                      </li>
                      <li>
                        On Submission of Payment Information: For processing
                        payments to affiliate marketers or receiving payments
                        from our clients, we collect payment channel details and
                        corresponding financial information.
                      </li>
                    </ol>
                  </Typography>

                  {/* --- NEW: User Generated Content + Communications --- */}
                  <Typography component="dd" paragraph>
                    <strong>
                      (2.2) User-Generated Content and Communications:
                    </strong>{" "}
                    We collect and process information you choose to provide on
                    the platform such as profile content, photographs, messages,
                    chat communications, and other content you submit, post, or
                    transmit through Beramour. We may also process metadata
                    associated with these communications (for example, time
                    sent, time received, and delivery status) to operate and
                    secure the service.
                  </Typography>

                  <Typography component="dd" paragraph>
                    <strong>(2.3) Usage Data:</strong> We collect information
                    about how you use our services and referring Uniform
                    Resource Locators (URLs). This may include device
                    information, browser type, operating system information,
                    language settings, pages or screens viewed, access times,
                    and approximate location inferred from IP address.
                  </Typography>
                  <Typography component="dd" paragraph>
                    <strong>(2.4) Cookies and Tracking Technologies:</strong> We
                    use cookies and similar tracking technologies to enhance
                    user experience, analyze usage, personalize content, and
                    support referral and affiliate attribution. You can control
                    cookies through your browser settings, but disabling cookies
                    may affect certain functionality.
                  </Typography>

                  {/* --- NEW: Security / Device / Login Data --- */}
                  <Typography component="dd" paragraph>
                    <strong>(2.5) Security and Authentication Data:</strong> To
                    help protect accounts and prevent fraud, we may process
                    security-related information such as login history,
                    authentication events, device identifiers, IP addresses, and
                    other signals that help us verify account access and detect
                    suspicious activity.
                  </Typography>

                  {/* --- NEW: Reports + Safety Signals --- */}
                  <Typography component="dd" paragraph>
                    <strong>
                      (2.6) Reports and Safety-Related Information:
                    </strong>{" "}
                    Where users report accounts, content, or behavior that
                    violates our Community Rules, we may collect and process
                    reports, supporting evidence (including relevant messages,
                    profile details, images), and related communications to
                    investigate and take enforcement action.
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(3). How We Use Your Information.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    <strong>
                      We use your information for the following purposes:{" "}
                    </strong>
                    <ol type="a">
                      <li>
                        To Provide and Improve Our Services: To operate our
                        platform, mobile applications, and affiliate marketing
                        module effectively, and to enhance the user experience.
                      </li>
                      <li>
                        To Process Payments: To manage payments to affiliates as
                        well as payments from users, and to ensure that
                        transactions are processed securely.
                      </li>
                      <li>
                        To Communicate with You: To send you updates and other
                        information related to our services. We do not use your
                        personal information for marketing purposes whatsoever.
                      </li>
                      <li>
                        To Analyze Usage: To understand how our services are
                        used and to improve our offerings.
                      </li>

                      {/* --- NEW: Security, Safety, Moderation, Referrals, Matching --- */}
                      <li>
                        To Protect Users and Maintain Safety: To detect,
                        prevent, investigate, and respond to suspected fraud,
                        harassment, scams, or other harmful activity; to process
                        reports; and to enforce our Community Rules.
                      </li>
                      <li>
                        To Secure Accounts: To authenticate users, prevent
                        unauthorized access, and maintain the integrity of the
                        platform.
                      </li>
                      <li>
                        To Operate Referrals and Affiliate Attribution: To
                        attribute referrals, prevent abuse of referral systems,
                        and maintain accurate accounting for affiliate programs.
                      </li>
                      <li>
                        To Power Recommendations and Matching: To recommend
                        profiles, prioritize content, and improve matching
                        relevance, including by using automated systems that
                        enhance user experience and platform safety.
                      </li>
                    </ol>
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(4). Data Sharing and Disclosure.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    <ol type="a">
                      <li>
                        With Service Providers: We may share your information
                        with third-party service providers who assist us in
                        operating our services, such as payment processors,
                        hosting providers, analytics providers, customer support
                        tools, and security vendors. These providers are
                        contractually obligated to protect your information and
                        use it solely for the purposes we specify.
                      </li>
                      <li>
                        For Legal Compliance: We may disclose your information
                        to comply with applicable laws, regulations, or legal
                        processes, or to respond to lawful requests by
                        government agencies.
                      </li>
                      <li>
                        Business Transfers: In the event of a merger,
                        acquisition, or other business reorganization, your
                        information may be transferred as part of the
                        transaction. This event is, however, highly unlikely.
                      </li>

                      {/* --- NEW: Safety + Emergency + Rights Protection --- */}
                      <li>
                        To Protect Rights, Safety, and Prevent Harm: We may
                        disclose personal information where we believe it is
                        necessary to protect the rights and safety of our users
                        or others, investigate fraud, respond to emergencies
                        involving potential harm, or enforce our policies.
                      </li>
                    </ol>
                    Outside the above scope, Beramour does not sell or share
                    your personal information with any other third parties
                    whatsoever.
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(5). Data Security.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    We implement appropriate technical and organizational
                    measures to protect your personal information against
                    unauthorized access, alteration, disclosure, or destruction.
                    However, no data transmission over the internet or
                    electronic storage is completely secure, and we therefore
                    cannot guarantee absolute security at all times.
                  </Typography>

                  {/* --- NEW: Account Responsibility Clause --- */}
                  <Typography component="dd" paragraph>
                    Users are responsible for keeping their login credentials
                    confidential and for restricting access to their devices. If
                    you believe your account has been compromised, please
                    contact us promptly using the contact details below.
                  </Typography>

                  {/* --- NEW: International Processing / Transfers --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(6). International Data Processing.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    While Beramour is operated from Kenya, some of our service
                    providers may process data outside Kenya (for example, cloud
                    hosting or payment infrastructure). Where cross-border
                    processing occurs, we take reasonable steps to ensure
                    appropriate safeguards consistent with applicable data
                    protection laws.
                  </Typography>

                  {/* --- Renumbered Retention onwards --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(7). Data Retention.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    We retain your personal information only for as long as
                    necessary to fulfill the purposes for which it was collected
                    or as required by law. If you close your account or cease
                    using our services, we may retain certain information for
                    legal, safety, compliance, and operational purposes for a
                    specific period, lasting about 6 calendar months after
                    account termination. Thereafter, your data is deleted at our
                    discretion, unless a longer retention period is required by
                    law or necessary for dispute resolution or investigations.
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(8). Your Rights.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Under applicable data protection laws, you may have the
                    following rights:
                    <ol type="a">
                      <li>
                        Access: You can request access to the personal
                        information we hold about you.
                      </li>
                      <li>
                        Correction: You can request correction of inaccurate or
                        incomplete information.
                      </li>
                      <li>
                        Deletion: You can personally delete your account at any
                        time. We retain certain information solely for legal,
                        safety, and compliance purposes for a period of about 6
                        calendar months.
                      </li>
                      <li>
                        Objection: You can object to the processing of your
                        personal information for certain purposes.
                      </li>
                      <li>
                        Restriction: You can request restriction of processing
                        your data under certain circumstances.
                      </li>
                      <li>
                        Data Portability: You can request a copy of your
                        personal information in a structured, commonly used, and
                        machine-readable format.
                      </li>
                    </ol>
                    To exercise these rights, please contact us using the
                    contact details provided at the bottom of this page.
                  </Typography>

                  {/* --- NEW: Automated Processing Clarification --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (9). Automated Processing and Recommendations.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Beramour may use automated systems to recommend profiles,
                    prioritize content, detect abuse, and improve matching
                    relevance. These processes are designed to enhance user
                    experience and platform safety and do not produce legal or
                    similarly significant effects without human review.
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(10). Children's Privacy.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Our services are not intended for individuals under the age
                    of 18. We do not knowingly collect or solicit personal
                    information from individuals under 18. If we become aware
                    that we have collected personal information from a child
                    under 18, we will take steps to delete such information
                    promptly.
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(11). Changes to This Privacy Policy.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    We may update this Privacy Policy from time to time to
                    reflect changes in our practices or legal requirements. We
                    will notify you of any material changes by posting the
                    updated content on this page. The date of the latest update
                    is indicated at the top. Your continued use of our services
                    after such changes constitutes your acceptance of the
                    revised Privacy Policy.
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(12). Governing Law.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    This Privacy Policy is governed by and construed in
                    accordance with the laws of Kenya. Any disputes arising out
                    of or in connection with this policy shall be subject to the
                    exclusive jurisdiction of the courts of Kenya. Legal
                    proceedings may only be filed in a court located within
                    Nairobi County, Kenya.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(13). Contact Details</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Beramour Dating Platform,
                    <br />
                    Beta Infinity Ltd,
                    <br />
                    PO Box 7129,
                    <br />
                    00200 City Square,
                    <br />
                    Nairobi, Kenya
                    <br />
                    Contact: support.beramour.com (Open a Ticket)
                    <br />
                    Beramour Dating Platform is a product of Beta Infinity Ltd.
                  </Typography>
                </Box>
              </Box>
              {/* --- Original Privacy Policy Content Ends Here --- */}
            </CardBody>
            {/* --- CardFooter removed as there's no link or other content --- */}
          </Card>
        </GridItem>
      </GridContainer>

      {/* --- SimpleAlert and alertMessage state removed --- */}
    </div>
  );
}
